import { Alert, AlertDescription, AlertIcon, Button, Spinner, Tag, useDisclosure, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CourtDto } from '../../../models/court/dto/court.dto';
import CourtAvailabilityTypePicker from '../../court-management/components/CourtAvailabilityTypePicker';
import { useBatchUpsertAvailabilitiesCompact } from '../../court-management/hooks/useBatchUpsertAvailabilitiesCompact';
import { useGetCourtCalendar } from '../../court-details/hooks/useGetCourtCalendar';
import { buildAvailabilitySaveWarning } from '../../court-details/utils/availability-save-warning';
import { blockRangeMinutes } from '../model';
import { usePricingGridState } from '../usePricingGridState';
import { buildGridCompactBlocks, buildGridTemplateRules, countGridCells } from '../utils/build-grid-compact-blocks';
import { seedGridState } from '../utils/seed-grid-state';
import CourtPricingGridEditor, { formatDateLabel } from './CourtPricingGridEditor';

type Props = { court: CourtDto };

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The grid editor for an EXISTING court, next to `AddAvailabilitiesModal` on
 * the court's pricing tab. Blocks are restored from the saved template and
 * every cell from the live availabilities of the week the period starts on,
 * so the grid opens showing what players can book today. Saving goes through
 * the same compact endpoint as the existing modal and, like it, replaces the
 * court's whole schedule between the two dates.
 */
export default function EditCourtPricingGridModal({ court }: Props) {
    const modal = useDisclosure();
    const toast = useToast();
    const grid = usePricingGridState();
    const { setState: setGridState } = grid;
    const sports = useMemo(() => court.sports ?? [], [court.sports]);

    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date(Date.now() + 364 * DAY_MS);
        date.setHours(23, 59, 59, 999);
        return date;
    });
    const [isGameAvailable, setIsGameAvailable] = useState(true);
    const [isCoachingAvailable, setIsCoachingAvailable] = useState(false);
    const [returnAsOneChunk, setReturnAsOneChunk] = useState(false);
    const [currency, setCurrency] = useState<Currency>(Currency.EUR);
    const [hasMisencodedRules, setHasMisencodedRules] = useState(false);
    const [seededWeekStart, setSeededWeekStart] = useState<Date | null>(null);
    // The API's own refusal text, kept ON the modal until the next attempt. A
    // corner toast alone was missed in testing, and a refused save then looked
    // exactly like a save that only reached one week.
    const [saveError, setSaveError] = useState<string | null>(null);

    // The week the grid is seeded from is fixed when the modal opens, so
    // shifting the period afterwards never re-seeds over in-progress edits.
    const weekStart = useMemo(() => (modal.isOpen ? startDate : null), [modal.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    const weekEnd = useMemo(() => (weekStart ? new Date(weekStart.getTime() + 7 * DAY_MS) : null), [weekStart]);
    // A refused read is rendered as a refusal, never as an empty grid: with no
    // week to seed from the save stays disabled and the notice says why.
    const { data: week, isLoading: isWeekLoading, isError: isWeekUnreadable } = useGetCourtCalendar(
        court.id,
        weekStart ?? startDate,
        weekEnd ?? startDate,
        modal.isOpen && weekStart !== null,
    );

    useEffect(() => {
        if (!modal.isOpen) {
            setSeededWeekStart(null);
            setSaveError(null);
            return;
        }
        if (seededWeekStart !== null || !week || !weekStart) return;
        const seed = seedGridState(court, week.availabilities ?? [], v4);
        setGridState(seed.state);
        setIsGameAvailable(seed.isGameAvailable);
        setIsCoachingAvailable(seed.isCoachingAvailable);
        setReturnAsOneChunk(seed.returnAsOneChunk);
        setCurrency(seed.currency);
        setHasMisencodedRules(seed.hasMisencodedRules);
        setSeededWeekStart(weekStart);
    }, [modal.isOpen, week, weekStart, seededWeekStart, court, setGridState]);

    const { mutateAsync: save, isLoading: isSaving } = useBatchUpsertAvailabilitiesCompact(court.id, (result) => {
        const warning = buildAvailabilitySaveWarning(result);
        const summary = translate('courts.pricing_grid.saved_summary', {
            count: result.count,
            start: formatDateLabel(startDate),
            end: formatDateLabel(endDate),
        });
        toast({
            title: translate('availabilities_edited_successfully'),
            description: warning ? `${summary} ${warning}` : summary,
            status: warning ? 'warning' : 'success',
            duration: warning ? 12000 : 6000,
            isClosable: true,
            position: 'top-right',
        });
        modal.onClose();
    }, (error) => {
        const message = error instanceof Error && error.message ? error.message : translate('error');
        setSaveError(message);
        toast({
            title: translate('courts.pricing_grid.save_refused'),
            description: message,
            status: 'error',
            duration: 8000,
            isClosable: true,
            position: 'top-right',
        });
    });

    const counts = countGridCells(grid.state, sports);
    const hasInvalidBlock = grid.state.blocks.some((b) => {
        const range = blockRangeMinutes(b);
        return range === null || range[0] >= range[1];
    });
    const canSubmit = seededWeekStart !== null && (isGameAvailable || isCoachingAvailable) && !hasInvalidBlock && endDate >= startDate && counts.priced > 0 && sports.length > 0;

    const handleSubmit = () => {
        if (!canSubmit) {
            if (counts.priced === 0) {
                toast({ title: translate('courts.pricing_grid.nothing_priced'), status: 'error', duration: 5000, position: 'top-right' });
            }
            return;
        }
        setSaveError(null);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        save({
            timeZone,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            isGameAvailable,
            isCoachingAvailable,
            returnAsOneChunk,
            blocks: buildGridCompactBlocks({ state: grid.state, sports, currency, returnAsOneChunk }),
            availabilityTemplates: [{
                id: v4(),
                courtId: court.id,
                startDate,
                endDate,
                appliedToAllTime: true,
                rules: buildGridTemplateRules(grid.state, sports, currency, v4),
                isGameAvailable,
                isCoachingAvailable,
                returnAsOneChunk,
            }],
        });
    };

    return (
        <>
            <Button variant="lightBrand" onClick={modal.onOpen} rightIcon={<Tag size="sm" colorScheme="accent">{translate('courts.pricing_grid.beta_badge')}</Tag>}>
                {translate('courts.pricing_grid.edit_with_grid')}
            </Button>
            <FormModal
                isOpen={modal.isOpen}
                onClose={modal.onClose}
                title={`${translate('courts.pricing_grid.edit_with_grid')} — ${court.name}`}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isSaving}
                isSubmitDisabled={!canSubmit}
                size="5xl"
            >
                <FormStack density="section">
                    {saveError && (
                        <Alert status="error" borderRadius="md" data-testid="pricing-grid-save-error">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                <strong>{translate('courts.pricing_grid.save_refused')}</strong> — {saveError}
                            </AlertDescription>
                        </Alert>
                    )}
                    {hasMisencodedRules && (
                        <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">{translate('courts.availability_hours_may_be_wrong')}</AlertDescription>
                        </Alert>
                    )}
                    <CourtAvailabilityTypePicker
                        isGameAvailable={isGameAvailable}
                        setIsGameAvailable={setIsGameAvailable}
                        isCoachingAvailable={isCoachingAvailable}
                        setIsCoachingAvailable={setIsCoachingAvailable}
                    />
                    <CourtPricingGridEditor
                        grid={grid}
                        sports={sports}
                        currency={currency}
                        returnAsOneChunk={returnAsOneChunk}
                        onReturnAsOneChunkChange={setReturnAsOneChunk}
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                        notice={
                            isWeekUnreadable ? (
                                <Alert status="error" borderRadius="md" mb={3} data-testid="pricing-grid-week-unreadable">
                                    <AlertIcon />
                                    <AlertDescription fontSize="sm">{translate('courts.pricing_grid.week_read_failed')}</AlertDescription>
                                </Alert>
                            ) : seededWeekStart === null || isWeekLoading ? (
                                <Alert status="info" borderRadius="md" mb={3}>
                                    <Spinner size="sm" mr={3} />
                                    <AlertDescription fontSize="sm">{translate('courts.pricing_grid.loading_week')}</AlertDescription>
                                </Alert>
                            ) : (
                                <Alert status="warning" borderRadius="md" mb={3}>
                                    <AlertIcon />
                                    <AlertDescription fontSize="sm">
                                        {translate('courts.pricing_grid.seeded_from_week', { date: formatDateLabel(seededWeekStart) })}
                                    </AlertDescription>
                                </Alert>
                            )
                        }
                    />
                </FormStack>
            </FormModal>
        </>
    );
}
